import type * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import * as store from '@attain-apps/fe/web/main/lib/store'
import * as rxjsUtils from '@attain-apps/rxjs-utils'
import { TypeChecker } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import * as organizations from '../../../organizations'
import type * as occupants from '../../occupants'
import type * as recordAssignedOrgs from '../../record-assigned-orgs'
import type * as responsiblePersons from '../../responsible-persons'
import type { TypeParams } from '../types'

/**
 * When the session starts, if the user is creating a responsible person or
 * occupant, we want to set the record department filter to the departments that
 * the user occupies. This way, when they create a responsible person or
 * occupant, they will only see the departments that they occupy in the
 * department filter dropdown.
 */
export const setRecordDepartmentFilterOnSessionStart = store.effects.create(
  (
    _: ec.Collection<TypeParams>['feature'],
    collection: ec.Collection<TypeParams>,
    occupantsEc: occupants.recordOccupants.Collection,
    responsiblePersonsEc: responsiblePersons.recordResponsiblePersons.Collection,
    recordAssignedOrgsEc: recordAssignedOrgs.Collection,
  ) =>
    rxjs
      .merge(
        responsiblePersonsEc.plugins.currentEntityPlugin.feature.selectors.isCreating()
          .asObservable,
        occupantsEc.plugins.currentEntityPlugin.feature.selectors.isCreating()
          .asObservable,
      )
      .pipe(
        rxjsUtils.delayUntil(
          collection.plugins.currentListPlugin.appliedParams.initialized$,
        ),
        rxjs.filter(TypeChecker.isTrue),
        rxjs.withLatestFrom(
          recordAssignedOrgsEc.plugins.currentListPlugin.feature.selectors
            .entities.asObservable,
        ),
        rxjs.map(([, assignments]) => {
          const occupierOrgIds = [
            ...new Set(
              assignments
                .filter(({ isOccupier }) => isOccupier)
                .map(
                  ({ organizationLookup }) =>
                    organizationLookup[
                      organizations.constants.DEPARTMENT_ENTITY_ID
                    ],
                ),
            ),
          ]
          return collection.plugins.currentListPlugin.appliedParams.actions.writeAllProperties
            .from('setRecordDepartmentFilterOnSessionStart (effect)')
            .create({
              values: {
                filters: {
                  orgFilter: occupierOrgIds.length
                    ? {
                        entityId: organizations.constants.DEPARTMENT_ENTITY_ID,
                        recordIds: occupierOrgIds,
                      }
                    : undefined,
                },
              },
            })
        }),
      ),
)
